import React from 'react';

// SHIFT Framework Overview Diagram
export const SHIFTFrameworkDiagram = ({ isFullscreen = false }: { isFullscreen?: boolean }) => {
  const baseColor = isFullscreen ? '#1F2937' : '#2C3E50'; // Darker gray for fullscreen text contrast
  const accentColors = {
    sensing: '#3498DB',
    homeostatic: '#2ECC71',
    inflammation: '#E74C3C',
    fibrosis: '#9B59B6',
    timekeeping: '#F39C12'
  };

  return (
    <div className="flex justify-center my-8">
      <svg width="800" height="600" viewBox="0 0 800 600" className="max-w-full h-auto">
        {/* Background */}
        <rect width="800" height="600" fill="transparent" rx="10"/>
        
        {/* Central Hub - Aging */}
        <circle cx="400" cy="300" r="80" fill={isFullscreen ? '#f8f9fa' : '#f8f9fa'} stroke={baseColor} strokeWidth="3"/>
        <text x="400" y="295" textAnchor="middle" className="text-base font-bold" fill={baseColor}>AGING</text>
        <text x="400" y="315" textAnchor="middle" className="text-xs" fill={baseColor}>BIOLOGY</text>
        
        {/* SHIFT Pillars */}
        {/* Sensing */}
        <g transform="translate(150, 120)">
          <circle cx="0" cy="0" r="60" fill={accentColors.sensing} fillOpacity="0.1" stroke={accentColors.sensing} strokeWidth="2"/>
          <text x="0" y="-5" textAnchor="middle" className="text-xs font-bold" fill={accentColors.sensing}>SENSING</text>
          <text x="0" y="15" textAnchor="middle" className="text-[10px]" fill={accentColors.sensing}>Nutrient Detection</text>
          <text x="0" y="30" textAnchor="middle" className="text-[10px]" fill={accentColors.sensing}>mTOR • AMPK • IGF-1</text>
          
          {/* Molecular icons */}
          <circle cx="-20" cy="-35" r="8" fill={accentColors.sensing} fillOpacity="0.3"/>
          <circle cx="20" cy="-35" r="8" fill={accentColors.sensing} fillOpacity="0.3"/>
          <circle cx="0" cy="-45" r="6" fill={accentColors.sensing} fillOpacity="0.5"/>
        </g>
        
        {/* Homeostatic Defense */}
        <g transform="translate(650, 120)">
          <circle cx="0" cy="0" r="60" fill={accentColors.homeostatic} fillOpacity="0.1" stroke={accentColors.homeostatic} strokeWidth="2"/>
          <text x="0" y="-5" textAnchor="middle" className="text-xs font-bold" fill={accentColors.homeostatic}>HOMEOSTATIC</text>
          <text x="0" y="10" textAnchor="middle" className="text-xs font-bold" fill={accentColors.homeostatic}>DEFENSE</text>
          <text x="0" y="25" textAnchor="middle" className="text-[10px]" fill={accentColors.homeostatic}>Autophagy • Stress Response</text>
          <text x="0" y="40" textAnchor="middle" className="text-[10px]" fill={accentColors.homeostatic}>Nrf2 • Heat Shock</text>
          
          {/* Cellular defense icons */}
          <rect x="-15" y="-40" width="10" height="10" fill={accentColors.homeostatic} fillOpacity="0.4" rx="2"/>
          <rect x="5" y="-40" width="10" height="10" fill={accentColors.homeostatic} fillOpacity="0.4" rx="2"/>
          <circle cx="0" cy="-50" r="4" fill={accentColors.homeostatic}/>
        </g>
        
        {/* Inflammation */}
        <g transform="translate(150, 480)">
          <circle cx="0" cy="0" r="60" fill={accentColors.inflammation} fillOpacity="0.1" stroke={accentColors.inflammation} strokeWidth="2"/>
          <text x="0" y="-5" textAnchor="middle" className="text-xs font-bold" fill={accentColors.inflammation}>INFLAMMATION</text>
          <text x="0" y="15" textAnchor="middle" className="text-[10px]" fill={accentColors.inflammation}>Inflammaging</text>
          <text x="0" y="30" textAnchor="middle" className="text-[10px]" fill={accentColors.inflammation}>TNF-α • IL-6 • CRP</text>
          
          {/* Inflammatory signals */}
          <path d="M -20,-35 L -10,-25 L -20,-15" stroke={accentColors.inflammation} strokeWidth="2" fill="none"/>
          <path d="M 20,-35 L 10,-25 L 20,-15" stroke={accentColors.inflammation} strokeWidth="2" fill="none"/>
          <circle cx="0" cy="-25" r="3" fill={accentColors.inflammation}/>
        </g>
        
        {/* Fibrosis */}
        <g transform="translate(650, 480)">
          <circle cx="0" cy="0" r="60" fill={accentColors.fibrosis} fillOpacity="0.1" stroke={accentColors.fibrosis} strokeWidth="2"/>
          <text x="0" y="-5" textAnchor="middle" className="text-xs font-bold" fill={accentColors.fibrosis}>FIBROSIS</text>
          <text x="0" y="15" textAnchor="middle" className="text-[10px]" fill={accentColors.fibrosis}>Tissue Scarring</text>
          <text x="0" y="30" textAnchor="middle" className="text-[10px]" fill={accentColors.fibrosis}>Collagen • ECM</text>
          
          {/* Fibrotic network */}
          <path d="M -25,-30 Q 0,-40 25,-30" stroke={accentColors.fibrosis} strokeWidth="1.5" fill="none"/>
          <path d="M -25,-20 Q 0,-10 25,-20" stroke={accentColors.fibrosis} strokeWidth="1.5" fill="none"/>
          <circle cx="-25" cy="-30" r="2" fill={accentColors.fibrosis}/>
          <circle cx="25" cy="-30" r="2" fill={accentColors.fibrosis}/>
        </g>
        
        {/* Timekeeping */}
        <g transform="translate(400, 80)">
          <circle cx="0" cy="0" r="60" fill={accentColors.timekeeping} fillOpacity="0.1" stroke={accentColors.timekeeping} strokeWidth="2"/>
          <text x="0" y="-5" textAnchor="middle" className="text-xs font-bold" fill={accentColors.timekeeping}>TIMEKEEPING</text>
          <text x="0" y="15" textAnchor="middle" className="text-[10px]" fill={accentColors.timekeeping}>Circadian Rhythms</text>
          <text x="0" y="30" textAnchor="middle" className="text-[10px]" fill={accentColors.timekeeping}>Epigenetic Clock</text>
          
          {/* Clock representation */}
          <circle cx="0" cy="-35" r="15" stroke={accentColors.timekeeping} strokeWidth="2" fill="none"/>
          <line x1="0" y1="-35" x2="0" y2="-45" stroke={accentColors.timekeeping} strokeWidth="2"/>
          <line x1="0" y1="-35" x2="8" y2="-30" stroke={accentColors.timekeeping} strokeWidth="1.5"/>
        </g>
        
        {/* Connecting Lines */}
        <line x1="320" y1="300" x2="210" y2="180" stroke={baseColor} strokeWidth="2" strokeDasharray="5,5" opacity="0.6"/>
        <line x1="480" y1="300" x2="590" y2="180" stroke={baseColor} strokeWidth="2" strokeDasharray="5,5" opacity="0.6"/>
        <line x1="320" y1="300" x2="210" y2="420" stroke={baseColor} strokeWidth="2" strokeDasharray="5,5" opacity="0.6"/>
        <line x1="480" y1="300" x2="590" y2="420" stroke={baseColor} strokeWidth="2" strokeDasharray="5,5" opacity="0.6"/>
        <line x1="400" y1="220" x2="400" y2="140" stroke={baseColor} strokeWidth="2" strokeDasharray="5,5" opacity="0.6"/>
        
        {/* GLP-1 RA Impact Overlay */}
        <g opacity="0.8">
          <circle cx="400" cy="300" r="120" fill="none" stroke="#00a8cc" strokeWidth="3" strokeDasharray="10,5"/>
          <text x="400" y="440" textAnchor="middle" className="text-sm font-bold" fill="#00a8cc">GLP-1 RA Multi-Target Impact</text>
        </g>
        
        {/* Legend */}
        <g transform="translate(50, 550)">
          <text x="0" y="0" className="text-[10px] font-semibold" fill={baseColor}>SHIFT Framework: Five Pillars of Aging Biology</text>
          <circle cx="300" cy="-3" r="3" fill="#00a8cc"/>
          <text x="310" y="0" className="text-[10px]" fill="#00a8cc">GLP-1 RA Therapeutic Target</text>
        </g>
      </svg>
    </div>
  );
};

// GLP-1 Receptor Signaling Pathway
export const GLP1SignalingDiagram = ({ isFullscreen = false }: { isFullscreen?: boolean }) => {
  const baseColor = isFullscreen ? '#1F2937' : '#2C3E50'; // Darker gray for fullscreen text contrast
  const pathwayColors = {
    glp1: '#3498DB',
    camp: '#2ECC71',
    pka: '#E74C3C',
    creb: '#9B59B6',
    downstream: '#F39C12'
  };

  return (
    <div className="flex justify-center my-8">
      <svg width="700" height="500" viewBox="0 0 700 500" className="max-w-full h-auto">
        {/* Background */}
        <rect width="700" height="500" fill="transparent" rx="10"/>
        
        {/* Cell Membrane */}
        <rect x="50" y="200" width="600" height="6" fill={baseColor} opacity="0.3"/>
        <text x="60" y="195" className="text-[10px]" fill={baseColor}>Cell Membrane</text>
        
        {/* GLP-1 Ligand */}
        <g transform="translate(100, 150)">
          <ellipse cx="0" cy="0" rx="25" ry="15" fill={pathwayColors.glp1} fillOpacity="0.7"/>
          <text x="0" y="0" textAnchor="middle" className="text-[10px] font-bold" fill="white">GLP-1</text>
          <text x="0" y="-35" textAnchor="middle" className="text-xs font-semibold" fill={pathwayColors.glp1}>Incretin Hormone</text>
        </g>
        
        {/* GLP-1 Receptor */}
        <g transform="translate(200, 203)">
          <rect x="-15" y="-30" width="30" height="60" fill={pathwayColors.glp1} fillOpacity="0.5" rx="5"/>
          <circle cx="0" cy="-35" r="8" fill={pathwayColors.glp1}/>
          <circle cx="0" cy="35" r="8" fill={pathwayColors.glp1}/>
          <text x="40" y="0" className="text-[10px] font-semibold" fill={pathwayColors.glp1}>GLP-1R</text>
          <text x="40" y="15" className="text-[10px]" fill={pathwayColors.glp1}>7-TM GPCR</text>
        </g>
        
        {/* G-protein Complex */}
        <g transform="translate(200, 280)">
          <circle cx="-20" cy="0" r="12" fill={pathwayColors.camp} fillOpacity="0.6"/>
          <circle cx="0" cy="0" r="12" fill={pathwayColors.camp} fillOpacity="0.6"/>
          <circle cx="20" cy="0" r="12" fill={pathwayColors.camp} fillOpacity="0.6"/>
          <text x="0" y="25" textAnchor="middle" className="text-[10px] font-semibold" fill={pathwayColors.camp}>Gαs Protein</text>
        </g>
        
        {/* Adenylyl Cyclase */}
        <g transform="translate(300, 280)">
          <rect x="-20" y="-15" width="40" height="30" fill={pathwayColors.camp} fillOpacity="0.4" rx="5"/>
          <text x="0" y="0" textAnchor="middle" className="text-[10px] font-bold" fill={pathwayColors.camp}>AC</text>
          <text x="0" y="35" textAnchor="middle" className="text-[10px]" fill={pathwayColors.camp}>Adenylyl Cyclase</text>
        </g>
        
        {/* cAMP */}
        <g transform="translate(400, 280)">
          <circle cx="0" cy="0" r="15" fill={pathwayColors.camp} fillOpacity="0.7"/>
          <text x="0" y="0" textAnchor="middle" className="text-[10px] font-bold" fill="white">cAMP</text>
          <text x="0" y="30" textAnchor="middle" className="text-[10px]" fill={pathwayColors.camp}>Second Messenger</text>
        </g>
        
        {/* PKA */}
        <g transform="translate(500, 280)">
          <rect x="-18" y="-12" width="36" height="24" fill={pathwayColors.pka} fillOpacity="0.6" rx="4"/>
          <text x="0" y="0" textAnchor="middle" className="text-[10px] font-bold" fill="white">PKA</text>
          <text x="0" y="30" textAnchor="middle" className="text-[10px]" fill={pathwayColors.pka}>Protein Kinase A</text>
        </g>
        
        {/* Nuclear Effects */}
        <g transform="translate(500, 380)">
          <circle cx="0" cy="0" r="25" fill={pathwayColors.creb} fillOpacity="0.2" stroke={pathwayColors.creb} strokeWidth="2"/>
          <text x="0" y="-5" textAnchor="middle" className="text-[10px] font-bold" fill={pathwayColors.creb}>CREB</text>
          <text x="0" y="8" textAnchor="middle" className="text-[10px]" fill={pathwayColors.creb}>Transcription</text>
          <text x="0" y="45" textAnchor="middle" className="text-[10px]" fill={pathwayColors.creb}>Nucleus</text>
        </g>
        
        {/* Downstream Effects */}
        <g transform="translate(150, 380)">
          <rect x="0" y="0" width="200" height="80" fill={pathwayColors.downstream} fillOpacity="0.1" stroke={pathwayColors.downstream} strokeWidth="1" rx="5"/>
          <text x="100" y="20" textAnchor="middle" className="text-xs font-bold" fill={pathwayColors.downstream}>Downstream Effects</text>
          <text x="30" y="40" className="text-[10px]" fill={pathwayColors.downstream}>• Insulin Release</text>
          <text x="30" y="55" className="text-[10px]" fill={pathwayColors.downstream}>• Glucagon Suppression</text>
          <text x="30" y="70" className="text-[10px]" fill={pathwayColors.downstream}>• Gastric Emptying ↓</text>
          <text x="120" y="40" className="text-[10px]" fill={pathwayColors.downstream}>• Satiety ↑</text>
          <text x="120" y="55" className="text-[10px]" fill={pathwayColors.downstream}>• β-cell Preservation</text>
          <text x="120" y="70" className="text-[10px]" fill={pathwayColors.downstream}>• Neuroprotection</text>
        </g>
        
        {/* Signal Flow Arrows */}
        <defs>
          <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill={baseColor} opacity="0.7"/>
          </marker>
        </defs>
        
        <path d="M 125,150 Q 160,175 185,190" stroke={baseColor} strokeWidth="2" fill="none" markerEnd="url(#arrowhead)"/>
        <path d="M 200,235 L 200,260" stroke={baseColor} strokeWidth="2" fill="none" markerEnd="url(#arrowhead)"/>
        <path d="M 220,280 L 280,280" stroke={baseColor} strokeWidth="2" fill="none" markerEnd="url(#arrowhead)"/>
        <path d="M 320,280 L 385,280" stroke={baseColor} strokeWidth="2" fill="none" markerEnd="url(#arrowhead)"/>
        <path d="M 415,280 L 482,280" stroke={baseColor} strokeWidth="2" fill="none" markerEnd="url(#arrowhead)"/>
        <path d="M 500,295 L 500,355" stroke={baseColor} strokeWidth="2" fill="none" markerEnd="url(#arrowhead)"/>
        <path d="M 480,290 Q 350,330 250,360" stroke={baseColor} strokeWidth="2" fill="none" markerEnd="url(#arrowhead)"/>
        
        {/* Title */}
        <text x="350" y="30" textAnchor="middle" className="text-base font-bold" fill={baseColor}>GLP-1 Receptor Signaling Cascade</text>
        <text x="350" y="50" textAnchor="middle" className="text-xs" fill={baseColor}>cAMP/PKA-Dependent Pathway</text>
      </svg>
    </div>
  );
};

// Autophagy and Cellular Defense Mechanisms
export const AutophagyDiagram = ({ isFullscreen = false }: { isFullscreen?: boolean }) => {
  const baseColor = isFullscreen ? '#1F2937' : '#2C3E50'; // Darker gray for fullscreen text contrast
  const organelleColors = {
    autophagosome: '#3498DB',
    lysosome: '#E74C3C',
    mitochondria: '#2ECC71',
    er: '#9B59B6',
    nucleus: '#F39C12'
  };

  return (
    <div className="flex justify-center my-8">
      <svg width="800" height="600" viewBox="0 0 800 600" className="max-w-full h-auto">
        {/* Background */}
        <rect width="800" height="600" fill="transparent" rx="10"/>
        
        {/* Cell Outline */}
        <ellipse cx="400" cy="300" rx="350" ry="250" fill="none" stroke={baseColor} strokeWidth="2" opacity="0.3"/>
        
        {/* Nucleus */}
        <g transform="translate(400, 300)">
          <circle cx="0" cy="0" r="60" fill={organelleColors.nucleus} fillOpacity="0.2" stroke={organelleColors.nucleus} strokeWidth="2"/>
          <text x="0" y="-5" textAnchor="middle" className="text-xs font-bold" fill={organelleColors.nucleus}>Nucleus</text>
          <text x="0" y="10" textAnchor="middle" className="text-[10px]" fill={organelleColors.nucleus}>CREB → Gene Expression</text>
          <text x="0" y="25" textAnchor="middle" className="text-[10px]" fill={organelleColors.nucleus}>Autophagy Genes ↑</text>
        </g>
        
        {/* Damaged Mitochondria */}
        <g transform="translate(200, 200)">
          <ellipse cx="0" cy="0" rx="30" ry="15" fill={organelleColors.mitochondria} fillOpacity="0.3" stroke={organelleColors.mitochondria} strokeWidth="2" strokeDasharray="3,3"/>
          <text x="0" y="0" textAnchor="middle" className="text-[10px]" fill={organelleColors.mitochondria}>Damaged</text>
          <text x="0" y="30" textAnchor="middle" className="text-[10px] font-semibold" fill={organelleColors.mitochondria}>Mitochondrion</text>
          
          {/* Damage indicators */}
          <circle cx="-10" cy="-5" r="3" fill="#E74C3C" opacity="0.7"/>
          <circle cx="8" cy="3" r="2" fill="#E74C3C" opacity="0.7"/>
        </g>
        
        {/* Phagophore Formation */}
        <g transform="translate(250, 200)">
          <path d="M -20,-10 Q 0,-25 20,-10 Q 15,5 0,15 Q -15,5 -20,-10" 
                fill={organelleColors.autophagosome} fillOpacity="0.4" stroke={organelleColors.autophagosome} strokeWidth="2"/>
          <text x="0" y="35" textAnchor="middle" className="text-[10px]" fill={organelleColors.autophagosome}>Phagophore</text>
        </g>
        
        {/* Autophagosome */}
        <g transform="translate(350, 200)">
          <circle cx="0" cy="0" r="25" fill={organelleColors.autophagosome} fillOpacity="0.3" stroke={organelleColors.autophagosome} strokeWidth="3"/>
          <ellipse cx="-5" cy="0" rx="8" ry="5" fill={organelleColors.mitochondria} fillOpacity="0.5"/>
          <text x="0" y="40" textAnchor="middle" className="text-[10px] font-semibold" fill={organelleColors.autophagosome}>Autophagosome</text>
          <text x="0" y="55" textAnchor="middle" className="text-[10px]" fill={organelleColors.autophagosome}>Double Membrane</text>
        </g>
        
        {/* Lysosome */}
        <g transform="translate(500, 200)">
          <circle cx="0" cy="0" r="20" fill={organelleColors.lysosome} fillOpacity="0.4" stroke={organelleColors.lysosome} strokeWidth="2"/>
          <text x="0" y="0" textAnchor="middle" className="text-[10px] font-bold" fill="white">H+</text>
          <text x="0" y="35" textAnchor="middle" className="text-[10px] font-semibold" fill={organelleColors.lysosome}>Lysosome</text>
          <text x="0" y="50" textAnchor="middle" className="text-[10px]" fill={organelleColors.lysosome}>Acidic pH</text>
          
          {/* Acid indicators */}
          <circle cx="-8" cy="-5" r="2" fill={organelleColors.lysosome} opacity="0.8"/>
          <circle cx="6" cy="4" r="1.5" fill={organelleColors.lysosome} opacity="0.8"/>
        </g>
        
        {/* Autolysosome */}
        <g transform="translate(580, 200)">
          <circle cx="0" cy="0" r="28" fill={organelleColors.lysosome} fillOpacity="0.2" stroke={organelleColors.lysosome} strokeWidth="3"/>
          <circle cx="0" cy="0" r="15" fill={organelleColors.autophagosome} fillOpacity="0.3"/>
          <text x="0" y="45" textAnchor="middle" className="text-[10px] font-semibold" fill={organelleColors.lysosome}>Autolysosome</text>
          <text x="0" y="60" textAnchor="middle" className="text-[10px]" fill={organelleColors.lysosome}>Degradation</text>
        </g>
        
        {/* Recycled Components */}
        <g transform="translate(650, 300)">
          <circle cx="0" cy="0" r="4" fill="#2ECC71"/>
          <circle cx="15" cy="-10" r="3" fill="#3498DB"/>
          <circle cx="-10" cy="8" r="3" fill="#E74C3C"/>
          <circle cx="8" cy="12" r="2" fill="#F39C12"/>
          <text x="0" y="30" textAnchor="middle" className="text-[10px]" fill={baseColor}>Recycled</text>
          <text x="0" y="45" textAnchor="middle" className="text-[10px]" fill={baseColor}>Building Blocks</text>
        </g>
        
        {/* GLP-1 Signaling Input */}
        <g transform="translate(150, 400)">
          <rect x="0" y="0" width="120" height="60" fill="#00a8cc" fillOpacity="0.1" stroke="#00a8cc" strokeWidth="2" rx="5"/>
          <text x="60" y="20" textAnchor="middle" className="text-xs font-bold" fill="#00a8cc">GLP-1 RA</text>
          <text x="60" y="35" textAnchor="middle" className="text-[10px]" fill="#00a8cc">Activation</text>
          <text x="60" y="50" textAnchor="middle" className="text-[10px]" fill="#00a8cc">↓ ER Stress</text>
        </g>
        
        {/* Stress Response Pathways */}
        <g transform="translate(500, 400)">
          <rect x="0" y="0" width="150" height="80" fill={organelleColors.er} fillOpacity="0.1" stroke={organelleColors.er} strokeWidth="1" rx="5"/>
          <text x="75" y="20" textAnchor="middle" className="text-xs font-bold" fill={organelleColors.er}>Stress Responses</text>
          <text x="20" y="40" className="text-[10px]" fill={organelleColors.er}>• Nrf2 Activation</text>
          <text x="20" y="55" className="text-[10px]" fill={organelleColors.er}>• Heat Shock Proteins</text>
          <text x="20" y="70" className="text-[10px]" fill={organelleColors.er}>• Antioxidant Enzymes</text>
        </g>
        
        {/* Process Flow Arrows */}
        <defs>
          <marker id="arrowhead2" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill={baseColor} opacity="0.7"/>
          </marker>
        </defs>
        
        <path d="M 230,200 L 270,200" stroke={baseColor} strokeWidth="2" fill="none" markerEnd="url(#arrowhead2)"/>
        <path d="M 325,200 L 375,200" stroke={baseColor} strokeWidth="2" fill="none" markerEnd="url(#arrowhead2)"/>
        <path d="M 520,200 L 552,200" stroke={baseColor} strokeWidth="2" fill="none" markerEnd="url(#arrowhead2)"/>
        <path d="M 608,200 L 635,220" stroke={baseColor} strokeWidth="2" fill="none" markerEnd="url(#arrowhead2)"/>
        <path d="M 210,430 Q 300,350 380,250" stroke="#00a8cc" strokeWidth="2" fill="none" markerEnd="url(#arrowhead2)" strokeDasharray="5,5"/>
        
        {/* Title and Labels */}
        <text x="400" y="40" textAnchor="middle" className="text-base font-bold" fill={baseColor}>GLP-1 Enhanced Autophagy</text>
        <text x="400" y="60" textAnchor="middle" className="text-xs" fill={baseColor}>Cellular Quality Control and Stress Defense</text>
        
        {/* Process Steps */}
        <text x="200" y="170" textAnchor="middle" className="text-[10px] font-semibold" fill={baseColor}>1. Damage Detection</text>
        <text x="300" y="170" textAnchor="middle" className="text-[10px] font-semibold" fill={baseColor}>2. Engulfment</text>
        <text x="400" y="170" textAnchor="middle" className="text-[10px] font-semibold" fill={baseColor}>3. Vesicle Formation</text>
        <text x="550" y="170" textAnchor="middle" className="text-[10px] font-semibold" fill={baseColor}>4. Fusion & Digestion</text>
      </svg>
    </div>
  );
};

// Multi-Agonist Receptor Mechanisms
export const MultiAgonistDiagram = ({ isFullscreen = false }: { isFullscreen?: boolean }) => {
  const baseColor = isFullscreen ? '#1F2937' : '#2C3E50'; // Darker gray for fullscreen text contrast
  const receptorColors = {
    glp1: '#3498DB',
    gip: '#2ECC71', 
    glucagon: '#E74C3C'
  };

  return (
    <div className="flex justify-center my-8">
      <svg width="800" height="500" viewBox="0 0 800 500" className="max-w-full h-auto">
        {/* Background */}
        <rect width="800" height="500" fill="transparent" rx="10"/>
        
        {/* Retatrutide Molecule */}
        <g transform="translate(100, 100)">
          <ellipse cx="0" cy="0" rx="60" ry="30" fill="#9B59B6" fillOpacity="0.3" stroke="#9B59B6" strokeWidth="2"/>
          <text x="0" y="-5" textAnchor="middle" className="text-xs font-bold" fill="#9B59B6">Retatrutide</text>
          <text x="0" y="10" textAnchor="middle" className="text-[10px]" fill="#9B59B6">Triple Agonist</text>
          <text x="0" y="50" textAnchor="middle" className="text-[10px]" fill="#9B59B6">39 amino acids</text>
          <text x="0" y="65" textAnchor="middle" className="text-[10px]" fill="#9B59B6">~6 day half-life</text>
        </g>
        
        {/* Cell Membrane */}
        <rect x="200" y="200" width="500" height="6" fill={baseColor} opacity="0.3"/>
        <text x="210" y="195" className="text-[10px]" fill={baseColor}>Cell Membrane</text>
        
        {/* GLP-1 Receptor */}
        <g transform="translate(300, 203)">
          <rect x="-15" y="-30" width="30" height="60" fill={receptorColors.glp1} fillOpacity="0.5" rx="5"/>
          <circle cx="0" cy="-35" r="8" fill={receptorColors.glp1}/>
          <circle cx="0" cy="35" r="8" fill={receptorColors.glp1}/>
          <text x="0" y="-50" textAnchor="middle" className="text-[10px] font-bold" fill={receptorColors.glp1}>GLP-1R</text>
          <text x="0" y="55" textAnchor="middle" className="text-[10px]" fill={receptorColors.glp1}>Satiety</text>
          <text x="0" y="70" textAnchor="middle" className="text-[10px]" fill={receptorColors.glp1}>Glucose Control</text>
        </g>
        
        {/* GIP Receptor */}
        <g transform="translate(450, 203)">
          <rect x="-15" y="-30" width="30" height="60" fill={receptorColors.gip} fillOpacity="0.5" rx="5"/>
          <circle cx="0" cy="-35" r="8" fill={receptorColors.gip}/>
          <circle cx="0" cy="35" r="8" fill={receptorColors.gip}/>
          <text x="0" y="-50" textAnchor="middle" className="text-[10px] font-bold" fill={receptorColors.gip}>GIPR</text>
          <text x="0" y="55" textAnchor="middle" className="text-[10px]" fill={receptorColors.gip}>Insulin Sensitivity</text>
          <text x="0" y="70" textAnchor="middle" className="text-[10px]" fill={receptorColors.gip}>Reduced Nausea</text>
        </g>
        
        {/* Glucagon Receptor */}
        <g transform="translate(600, 203)">
          <rect x="-15" y="-30" width="30" height="60" fill={receptorColors.glucagon} fillOpacity="0.5" rx="5"/>
          <circle cx="0" cy="-35" r="8" fill={receptorColors.glucagon}/>
          <circle cx="0" cy="35" r="8" fill={receptorColors.glucagon}/>
          <text x="0" y="-50" textAnchor="middle" className="text-[10px] font-bold" fill={receptorColors.glucagon}>GCGR</text>
          <text x="0" y="55" textAnchor="middle" className="text-[10px]" fill={receptorColors.glucagon}>Energy Expenditure</text>
          <text x="0" y="70" textAnchor="middle" className="text-[10px]" fill={receptorColors.glucagon}>Lipolysis</text>
        </g>
        
        {/* Downstream Pathways */}
        <g transform="translate(300, 320)">
          <circle cx="0" cy="0" r="15" fill={receptorColors.glp1} fillOpacity="0.7"/>
          <text x="0" y="0" textAnchor="middle" className="text-[10px] font-bold" fill="white">cAMP</text>
          <path d="M 15,0 L 45,20" stroke={receptorColors.glp1} strokeWidth="2" markerEnd="url(#arrowhead)"/>
          <text x="50" y="25" className="text-[10px]" fill={receptorColors.glp1}>Appetite ↓</text>
        </g>
        
        <g transform="translate(450, 320)">
          <circle cx="0" cy="0" r="15" fill={receptorColors.gip} fillOpacity="0.7"/>
          <text x="0" y="0" textAnchor="middle" className="text-[10px] font-bold" fill="white">cAMP</text>
          <path d="M 15,0 L 45,20" stroke={receptorColors.gip} strokeWidth="2" markerEnd="url(#arrowhead)"/>
          <text x="50" y="25" className="text-[10px]" fill={receptorColors.gip}>Insulin ↑</text>
        </g>
        
        <g transform="translate(600, 320)">
          <circle cx="0" cy="0" r="15" fill={receptorColors.glucagon} fillOpacity="0.7"/>
          <text x="0" y="0" textAnchor="middle" className="text-[10px] font-bold" fill="white">cAMP</text>
          <path d="M 15,0 L 45,20" stroke={receptorColors.glucagon} strokeWidth="2" markerEnd="url(#arrowhead)"/>
          <text x="50" y="25" className="text-[10px]" fill={receptorColors.glucagon}>Thermogenesis ↑</text>
        </g>
        
        {/* Binding Lines */}
        <defs>
          <marker id="arrowhead3" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill={baseColor} opacity="0.7"/>
          </marker>
        </defs>
        
        <path d="M 130,120 Q 200,150 285,175" stroke="#9B59B6" strokeWidth="2" fill="none" markerEnd="url(#arrowhead3)" strokeDasharray="3,3"/>
        <path d="M 130,120 Q 300,140 435,175" stroke="#9B59B6" strokeWidth="2" fill="none" markerEnd="url(#arrowhead3)" strokeDasharray="3,3"/>
        <path d="M 130,120 Q 400,130 585,175" stroke="#9B59B6" strokeWidth="2" fill="none" markerEnd="url(#arrowhead3)" strokeDasharray="3,3"/>
        
        {/* Synergistic Effects Box */}
        <g transform="translate(200, 400)">
          <rect x="0" y="0" width="400" height="60" fill="#F39C12" fillOpacity="0.1" stroke="#F39C12" strokeWidth="2" rx="5"/>
          <text x="200" y="20" textAnchor="middle" className="text-xs font-bold" fill="#F39C12">Synergistic Metabolic Effects</text>
          <text x="50" y="40" className="text-[10px]" fill="#F39C12">• 24% weight loss (48 weeks)</text>
          <text x="200" y="40" className="text-[10px]" fill="#F39C12">• 82% liver fat reduction</text>
          <text x="320" y="40" className="text-[10px]" fill="#F39C12">• Superior to mono-agonists</text>
        </g>
        
        {/* Title */}
        <text x="400" y="40" textAnchor="middle" className="text-base font-bold" fill={baseColor}>Triple Receptor Agonism: Retatrutide Mechanism</text>
        <text x="400" y="60" textAnchor="middle" className="text-xs" fill={baseColor}>Simultaneous GLP-1R, GIPR, and GCGR Activation</text>
      </svg>
    </div>
  );
};

// Inflammation Pathway Network
export const InflammationPathwayDiagram = ({ isFullscreen = false }: { isFullscreen?: boolean }) => {
  const baseColor = isFullscreen ? '#1F2937' : '#2C3E50'; // Darker gray for fullscreen text contrast
  const inflammationColors = {
    cytokines: '#E74C3C',
    antiInflam: '#2ECC71',
    glp1: '#3498DB',
    pathways: '#9B59B6'
  };

  return (
    <div className="flex justify-center my-8">
      <svg width="700" height="600" viewBox="0 0 700 600" className="max-w-full h-auto">
        {/* Background */}
        <rect width="700" height="600" fill="transparent" rx="10"/>
        
        {/* Central Inflammation Hub */}
        <g transform="translate(350, 300)">
          <circle cx="0" cy="0" r="50" fill={inflammationColors.cytokines} fillOpacity="0.2" stroke={inflammationColors.cytokines} strokeWidth="3"/>
          <text x="0" y="-5" textAnchor="middle" className="text-xs font-bold" fill={inflammationColors.cytokines}>Inflammaging</text>
          <text x="0" y="10" textAnchor="middle" className="text-[10px]" fill={inflammationColors.cytokines}>Chronic Low-Grade</text>
          <text x="0" y="25" textAnchor="middle" className="text-[10px]" fill={inflammationColors.cytokines}>Inflammation</text>
        </g>
        
        {/* Pro-inflammatory Cytokines */}
        <g transform="translate(150, 150)">
          <circle cx="0" cy="0" r="30" fill={inflammationColors.cytokines} fillOpacity="0.3"/>
          <text x="0" y="-5" textAnchor="middle" className="text-[10px] font-bold" fill={inflammationColors.cytokines}>TNF-α</text>
          <text x="0" y="8" textAnchor="middle" className="text-[10px]" fill={inflammationColors.cytokines}>IL-6</text>
          <text x="0" y="50" textAnchor="middle" className="text-[10px]" fill={inflammationColors.cytokines}>Pro-inflammatory</text>
        </g>
        
        <g transform="translate(550, 150)">
          <circle cx="0" cy="0" r="30" fill={inflammationColors.cytokines} fillOpacity="0.3"/>
          <text x="0" y="-5" textAnchor="middle" className="text-[10px] font-bold" fill={inflammationColors.cytokines}>IL-1β</text>
          <text x="0" y="8" textAnchor="middle" className="text-[10px]" fill={inflammationColors.cytokines}>CRP</text>
          <text x="0" y="50" textAnchor="middle" className="text-[10px]" fill={inflammationColors.cytokines}>Acute Phase</text>
        </g>
        
        {/* Senescent Cells */}
        <g transform="translate(150, 450)">
          <polygon points="-25,-20 25,-20 30,0 25,20 -25,20 -30,0" 
                   fill={inflammationColors.cytokines} fillOpacity="0.2" stroke={inflammationColors.cytokines} strokeWidth="2"/>
          <text x="0" y="-5" textAnchor="middle" className="text-[10px] font-bold" fill={inflammationColors.cytokines}>Senescent</text>
          <text x="0" y="8" textAnchor="middle" className="text-[10px] font-bold" fill={inflammationColors.cytokines}>Cells</text>
          <text x="0" y="45" textAnchor="middle" className="text-[10px]" fill={inflammationColors.cytokines}>SASP Secretion</text>
        </g>
        
        {/* Visceral Fat */}
        <g transform="translate(550, 450)">
          <ellipse cx="0" cy="0" rx="35" ry="25" fill={inflammationColors.cytokines} fillOpacity="0.2" stroke={inflammationColors.cytokines} strokeWidth="2"/>
          <text x="0" y="-5" textAnchor="middle" className="text-[10px] font-bold" fill={inflammationColors.cytokines}>Visceral</text>
          <text x="0" y="8" textAnchor="middle" className="text-[10px] font-bold" fill={inflammationColors.cytokines}>Adipose</text>
          <text x="0" y="45" textAnchor="middle" className="text-[10px]" fill={inflammationColors.cytokines}>Adipokine Release</text>
        </g>
        
        {/* GLP-1 RA Intervention */}
        <g transform="translate(350, 100)">
          <rect x="-40" y="-20" width="80" height="40" fill={inflammationColors.glp1} fillOpacity="0.3" stroke={inflammationColors.glp1} strokeWidth="2" rx="10"/>
          <text x="0" y="-5" textAnchor="middle" className="text-xs font-bold" fill={inflammationColors.glp1}>GLP-1 RA</text>
          <text x="0" y="10" textAnchor="middle" className="text-[10px]" fill={inflammationColors.glp1}>Anti-inflammatory</text>
        </g>
        
        {/* Anti-inflammatory Effects */}
        <g transform="translate(350, 500)">
          <rect x="-60" y="-30" width="120" height="60" fill={inflammationColors.antiInflam} fillOpacity="0.2" stroke={inflammationColors.antiInflam} strokeWidth="2" rx="8"/>
          <text x="0" y="-10" textAnchor="middle" className="text-xs font-bold" fill={inflammationColors.antiInflam}>Protective Effects</text>
          <text x="-40" y="5" className="text-[10px]" fill={inflammationColors.antiInflam}>• IL-10 ↑</text>
          <text x="-40" y="20" className="text-[10px]" fill={inflammationColors.antiInflam}>• M2 Macrophages ↑</text>
          <text x="10" y="5" className="text-[10px]" fill={inflammationColors.antiInflam}>• Nrf2 Activation</text>
          <text x="10" y="20" className="text-[10px]" fill={inflammationColors.antiInflam}>• Antioxidants ↑</text>
        </g>
        
        {/* Neuro-immune Axis */}
        <g transform="translate(100, 300)">
          <ellipse cx="0" cy="0" rx="25" ry="15" fill={inflammationColors.pathways} fillOpacity="0.3"/>
          <text x="0" y="0" textAnchor="middle" className="text-[10px] font-bold" fill={inflammationColors.pathways}>Brain</text>
          <text x="0" y="30" textAnchor="middle" className="text-[10px]" fill={inflammationColors.pathways}>Vagus Nerve</text>
          <text x="0" y="45" textAnchor="middle" className="text-[10px]" fill={inflammationColors.pathways}>Modulation</text>
        </g>
        
        {/* Immune Cells */}
        <g transform="translate(600, 300)">
          <circle cx="-15" cy="0" r="12" fill={inflammationColors.pathways} fillOpacity="0.4"/>
          <circle cx="15" cy="0" r="12" fill={inflammationColors.pathways} fillOpacity="0.4"/>
          <text x="0" y="0" textAnchor="middle" className="text-[10px] font-bold" fill="white">T</text>
          <text x="0" y="30" textAnchor="middle" className="text-[10px]" fill={inflammationColors.pathways}>T-cells</text>
          <text x="0" y="45" textAnchor="middle" className="text-[10px]" fill={inflammationColors.pathways}>Macrophages</text>
        </g>
        
        {/* Pathway Connections */}
        <defs>
          <marker id="arrowhead4" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill={baseColor} opacity="0.7"/>
          </marker>
          <marker id="inhibit" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto">
            <line x1="0" y1="0" x2="0" y2="10" stroke={inflammationColors.glp1} strokeWidth="3"/>
          </marker>
        </defs>
        
        {/* Pro-inflammatory connections */}
        <path d="M 175,175 Q 250,230 310,270" stroke={inflammationColors.cytokines} strokeWidth="2" fill="none" markerEnd="url(#arrowhead4)"/>
        <path d="M 525,175 Q 450,230 390,270" stroke={inflammationColors.cytokines} strokeWidth="2" fill="none" markerEnd="url(#arrowhead4)"/>
        <path d="M 175,425 Q 250,370 310,330" stroke={inflammationColors.cytokines} strokeWidth="2" fill="none" markerEnd="url(#arrowhead4)"/>
        <path d="M 525,425 Q 450,370 390,330" stroke={inflammationColors.cytokines} strokeWidth="2" fill="none" markerEnd="url(#arrowhead4)"/>
        
        {/* GLP-1 inhibitory effects */}
        <path d="M 350,140 L 350,250" stroke={inflammationColors.glp1} strokeWidth="3" fill="none" markerEnd="url(#inhibit)"/>
        <path d="M 310,120 Q 200,140 175,175" stroke={inflammationColors.glp1} strokeWidth="2" fill="none" markerEnd="url(#inhibit)" strokeDasharray="4,4"/>
        <path d="M 390,120 Q 500,140 525,175" stroke={inflammationColors.glp1} strokeWidth="2" fill="none" markerEnd="url(#inhibit)" strokeDasharray="4,4"/>
        
        {/* Anti-inflammatory promotion */}
        <path d="M 350,350 L 350,470" stroke={inflammationColors.antiInflam} strokeWidth="3" fill="none" markerEnd="url(#arrowhead4)"/>
        
        {/* Neuro-immune connections */}
        <path d="M 125,300 L 300,300" stroke={inflammationColors.pathways} strokeWidth="2" fill="none" markerEnd="url(#arrowhead4)" strokeDasharray="3,3"/>
        <path d="M 400,300 L 575,300" stroke={inflammationColors.pathways} strokeWidth="2" fill="none" markerEnd="url(#arrowhead4)" strokeDasharray="3,3"/>
        
        {/* Title */}
        <text x="350" y="30" textAnchor="middle" className="text-base font-bold" fill={baseColor}>GLP-1 RA Anti-Inflammatory Network</text>
        <text x="350" y="50" textAnchor="middle" className="text-xs" fill={baseColor}>Multi-Modal Inflammaging Suppression</text>
        
        {/* Legend */}
        <g transform="translate(50, 550)">
          <line x1="0" y1="0" x2="15" y2="0" stroke={inflammationColors.cytokines} strokeWidth="2"/>
          <text x="20" y="5" className="text-[10px]" fill={inflammationColors.cytokines}>Pro-inflammatory</text>
          
          <line x1="120" y1="0" x2="135" y2="0" stroke={inflammationColors.glp1} strokeWidth="2"/>
          <text x="140" y="5" className="text-[10px]" fill={inflammationColors.glp1}>GLP-1 Inhibition</text>
          
          <line x1="250" y1="0" x2="265" y2="0" stroke={inflammationColors.antiInflam} strokeWidth="2"/>
          <text x="270" y="5" className="text-[10px]" fill={inflammationColors.antiInflam}>Anti-inflammatory</text>
        </g>
      </svg>
    </div>
  );
};